/* PDP info tabs and FAQ accordion.

   Ported from prototype/pages/product-detail/product-detail.js
   (initialiseProductInformation). Accessible keyboard navigation preserved.
*/

(() => {
  if (window.CaratKinPdpTabs) return;
  window.CaratKinPdpTabs = true;

  /* ---- Card rails (mobile) ----------------------------------------------
     On phones the certification and material cards sit in a sideways
     scroll-snap strip (see .ck-card-rail in product-detail.css). This sets
     --ck-away on each card — 0 when centred, 1 when a full card away — so
     the neighbours shrink and fade continuously as they are swiped. On
     wider screens the strip is a plain grid and the property is unused. */
  const rails = new WeakMap(); // rail element -> repaint function

  function rail(el) {
    if (rails.has(el)) return rails.get(el);

    /* Phones (client, Oct 2026): cards line up with the left page edge and the
       next one peeks in at full size; a row of dots under the rail shows which
       card is in view and that there are more to swipe to. */
    const dots = document.createElement('div');
    dots.className = 'ck-dots';
    dots.setAttribute('aria-hidden', 'true');
    el.after(dots);

    const startOf = () => parseFloat(getComputedStyle(el).scrollPaddingLeft) || 0;

    function paint() {
      const cards = [...el.children];
      if (!el.clientWidth || !cards.length) return;
      const box = el.getBoundingClientRect();
      const start = box.left + startOf();
      const step = cards[0].offsetWidth + (parseFloat(getComputedStyle(el).columnGap) || 0);
      if (dots.children.length !== cards.length) {
        dots.replaceChildren(...cards.map(() => document.createElement('i')));
      }
      // At the far end the last card cannot reach the left edge; treat it as
      // current once the rail is scrolled all the way.
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
      cards.forEach((card, i) => {
        const r = card.getBoundingClientRect();
        const away = Math.min(1, Math.abs(r.left - start) / (step || 1));
        const current = el.scrollWidth > el.clientWidth && atEnd ? i === cards.length - 1 : away < 0.5;
        card.style.setProperty('--ck-away', away.toFixed(3));
        card.classList.toggle('is-current', current);
        dots.children[i].classList.toggle('is-active', current);
      });
      dots.hidden = el.scrollWidth <= el.clientWidth; // desktop grid or a single card
      el.setAttribute('data-ck-ready', '');
    }

    // Paint straight from the scroll event rather than requestAnimationFrame,
    // which browsers pause in background tabs.
    el.addEventListener('scroll', paint, { passive: true });
    window.addEventListener('resize', paint);

    // The site-wide scroll reveal fades cards in one by one; in a rail the
    // swipe drives them instead, so take them out of it.
    [...el.children].forEach((card) => {
      card.dataset.motionReady = 'true';
      card.classList.remove('ck-reveal');
    });

    el.addEventListener('click', (event) => {
      if (event.target.closest('button, a')) return; // a card's own button still works
      const card = [...el.children].find((c) => c.contains(event.target));
      if (!card || card.classList.contains('is-current')) return;
      if (el.scrollWidth <= el.clientWidth) return; // desktop grid: nothing to scroll
      event.preventDefault();
      el.scrollTo({ left: card.offsetLeft - startOf(), behavior: 'smooth' });
    });

    rails.set(el, paint);
    paint();
    return paint;
  }

  /* ---- Variant refresh ----------------------------------------------------
     Metal purity, colour and weight follow the selected variant, but the
     section is rendered once for the first one. When the shopper picks
     another, re-render just this section for that variant and swap in the
     parts that depend on it. */
  function watchVariant(root) {
    const cartBtn = document.querySelector('[data-pdp-cart]');
    const wrapper = root.closest('[id^="shopify-section-"]');
    if (!cartBtn || !wrapper) return;
    const sectionId = wrapper.id.replace('shopify-section-', '');
    const live = '[data-ck-live]';
    let last = cartBtn.dataset.variantId;
    let ticket = 0;

    new MutationObserver(async () => {
      const id = cartBtn.dataset.variantId;
      if (!id || id === last) return;
      last = id;
      const mine = ++ticket;
      try {
        const res = await fetch(
          `${window.location.pathname}?variant=${encodeURIComponent(id)}&section_id=${encodeURIComponent(sectionId)}`,
          { headers: { Accept: 'text/html' } }
        );
        if (!res.ok || mine !== ticket) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        if (mine !== ticket) return; // a newer choice has already won
        const fresh = [...doc.querySelectorAll(live)];
        root.querySelectorAll(live).forEach((el, i) => {
          if (!fresh[i]) return;
          el.innerHTML = fresh[i].innerHTML;
          rails.get(el)?.();
        });
      } catch {
        /* keep the figures already shown rather than blanking them */
      }
    }).observe(cartBtn, { attributes: true, attributeFilter: ['data-variant-id'] });
  }

  /* ---- Certificate popup ---------------------------------------------------
     "View Certificate" on a certification card opens a <dialog> with a sample
     certificate. Closes on the X, a click on the dimmed backdrop, or Esc. */
  function certificates(root) {
    root.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-ck-cert-open]');
      if (opener) {
        const dialog = document.getElementById(opener.dataset.ckCertOpen);
        if (dialog && !dialog.open) {
          dialog.showModal();
          document.documentElement.classList.add('ck-dialog-open');
        }
        return;
      }
      const dialog = event.target.closest('.ck-cert-dialog');
      // A click on the dialog element itself (not its content) is the backdrop.
      if (dialog && (event.target === dialog || event.target.closest('[data-ck-cert-close]'))) dialog.close();
    });
    root.querySelectorAll('.ck-cert-dialog').forEach((dialog) => {
      dialog.addEventListener('close', () => document.documentElement.classList.remove('ck-dialog-open'));
    });
  }

  /* ---- Know Your Material explorer ----------------------------------------
     Gold / Gemstone / Diamond sub-tabs, and the gemstone switcher when a
     piece has more than one stone. Both are plain show/hide; arrow keys move
     between the tabs of a list. */
  function materials(root) {
    const kym = root.querySelector('[data-kym]');
    if (!kym) return;

    function choose(list, attr, value, panels, panelAttr, focus) {
      list.forEach((btn) => {
        const on = btn.getAttribute(attr) === value;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-selected', String(on));
        btn.tabIndex = on ? 0 : -1;
        if (on && focus) btn.focus();
      });
      panels.forEach((p) => { p.hidden = p.getAttribute(panelAttr) !== value; });
    }

    [['[data-kym-tab]', 'data-kym-tab', '[data-kym-panel]', 'data-kym-panel'],
     ['[data-kym-gem]', 'data-kym-gem', '[data-kym-stone]', 'data-kym-stone']].forEach(([sel, attr, psel, pattr]) => {
      const list = [...kym.querySelectorAll(sel)];
      if (!list.length) return;
      const panels = [...kym.querySelectorAll(psel)];
      list.forEach((btn, i) => {
        btn.addEventListener('click', () => choose(list, attr, btn.getAttribute(attr), panels, pattr));
        btn.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const n = event.key === 'Home' ? 0 : event.key === 'End' ? list.length - 1
            : (i + (event.key === 'ArrowRight' ? 1 : -1) + list.length) % list.length;
          choose(list, attr, list[n].getAttribute(attr), panels, pattr, true);
        });
      });
    });
  }

  function init(root) {
    root.querySelectorAll('[data-ck-cards]').forEach(rail);
    watchVariant(root);
    certificates(root);
    materials(root);

    const tabList = root.querySelector('[data-product-tabs]');
    if (!tabList) return;

    const tabs = [...tabList.querySelectorAll('[data-product-tab]')];
    const panels = [...root.querySelectorAll('[data-product-panel]')];

    function activateTab(name, shouldFocus) {
      tabs.forEach((tab) => {
        const active = tab.dataset.productTab === name;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active) {
          if (shouldFocus) tab.focus();
          const targetLeft = Math.max(0, tab.offsetLeft - (tabList.clientWidth - tab.offsetWidth) / 2);
          tabList.scrollTo({ left: targetLeft, behavior: 'smooth' });
        }
      });
      panels.forEach((panel) => {
        const active = panel.dataset.productPanel === name;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
        // A rail measured while its panel was hidden has no sizes yet.
        if (active) panel.querySelectorAll('[data-ck-cards]').forEach((rail) => rails.get(rail)?.());
      });
    }

    tabList.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-product-tab]');
      if (tab) activateTab(tab.dataset.productTab);
    });

    /* Phones: the tabs become collapsible rows (client, Oct 2026). A row
       heading is added above each panel, named after its tab; each row opens
       and closes on its own, Description starting open. On wider screens the
       headings are hidden (CSS) and the tabs work as before. */
    const phone = window.matchMedia('(max-width: 767px)');
    const open = new Set(['description']);
    const heads = panels.map((panel) => {
      const name = panel.dataset.productPanel;
      const tab = tabs.find((t) => t.dataset.productTab === name);
      const head = document.createElement('button');
      head.type = 'button';
      head.className = 'pdp-acc';
      head.dataset.productAcc = name;
      head.setAttribute('aria-controls', panel.id);
      head.innerHTML = '<span></span><i aria-hidden="true"></i>';
      head.firstChild.textContent = tab ? tab.textContent.trim() : name;
      panel.before(head);
      // The panel's own title repeats the row heading; CSS hides it on phones.
      const title = panel.querySelector('h2');
      if (title && title.textContent.trim().toLowerCase() === head.firstChild.textContent.toLowerCase()) {
        title.classList.add('pdp-acc-dup');
      }
      return head;
    });

    function layout() {
      if (phone.matches) {
        panels.forEach((panel, i) => {
          const isOpen = open.has(panel.dataset.productPanel);
          panel.hidden = !isOpen;
          heads[i].setAttribute('aria-expanded', String(isOpen));
          heads[i].classList.toggle('is-open', isOpen);
          if (isOpen) panel.querySelectorAll('[data-ck-cards]').forEach((r) => rails.get(r)?.());
        });
      } else {
        const current = tabs.find((t) => t.getAttribute('aria-selected') === 'true');
        activateTab(current ? current.dataset.productTab : tabs[0].dataset.productTab);
      }
    }

    heads.forEach((head) => head.addEventListener('click', () => {
      const name = head.dataset.productAcc;
      if (open.has(name)) open.delete(name); else open.add(name);
      layout();
    }));

    if (phone.addEventListener) phone.addEventListener('change', layout);
    else phone.addListener(layout);
    layout();

    tabList.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const currentIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;
      activateTab(tabs[nextIndex].dataset.productTab, true);
    });

    // FAQ accordion
    root.querySelector('.pdp-faq__list')?.addEventListener('click', (event) => {
      const button = event.target.closest('button[aria-controls]');
      if (!button) return;
      const item = button.closest('.pdp-faq__item');
      const answer = document.getElementById(button.getAttribute('aria-controls'));
      if (!item || !answer) return;
      const expanded = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!expanded));
      item.classList.toggle('is-open', !expanded);
      answer.hidden = expanded;
    });
  }

  function mount() {
    const section = document.querySelector('.pdp-information');
    if (section) init(section);
  }

  mount();
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('.pdp-information')) init(event.target);
  });
})();
